import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import TableDesign, {
  TableHeading,
} from "../../../../components/common/table/TableDesign";
import {
  getAllReviwSourceAction,
  addReviewSourceAction,
  setAddReviewSourceResponse,
  setDeleteReviewResponse,
  deleteReviewAction,
} from "../../../../store/reducers/reviewSlice";
import { DeleteOutline } from "@mui/icons-material";
import CustomButton from "../../../../components/common/button/CustomButton";
import AlertPopup from "../../../../components/common/alertPopup/AlertPopup";
import Notification from "../../../../components/common/notification/Notification";
import { setNotificationAction } from "../../../../store/reducers/mmymSlice";

interface FilterOption {
  page: number;
  limit: number;
  orderBy: string;
  orderIn: string;
}

const ReviewSource = () => {
  const dispatch = useDispatch();
  const { reviewSource, isAddReviewSourceSuccess, isUpdateReviewSuccess } =
    useSelector((state: any) => state.review);

  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<any>(null);
  const [filterOption, setFilterOption] = useState<FilterOption>({
    page: 1,
    limit: 10,
    orderBy: "created_at",
    orderIn: "DESC",
  });

  const popupContent = {
    delete: "Are you sure you want to delete this review source?",
  };

  const tableHeadings: TableHeading[] = [
    { label: "#", align: "left" },
    { label: "Name", align: "left" },
    { label: "Action", align: "center" },
  ];

  useEffect(() => {
    if (isAddReviewSourceSuccess || isUpdateReviewSuccess) {
      dispatch(getAllReviwSourceAction());
      dispatch(setAddReviewSourceResponse(false));
      dispatch(setDeleteReviewResponse(false));
    }
  }, [isAddReviewSourceSuccess, isUpdateReviewSuccess]);

  useEffect(() => {
    dispatch(getAllReviwSourceAction());
    dispatch(setNotificationAction({ message: [], severity: "error" }));
  }, [dispatch]);

  const handleSave = () => {
    if (!name.trim()) {
      setNameError("Source name is required");

      setTimeout(() => {
        setNameError("");
      }, 5000);

      return;
    }

    dispatch(
      addReviewSourceAction({
        name: name,
      }),
    );

    setName("");
  };

  const handleDeleteClick = (id: number) => {
    setSelectedId(id);
    setOpen(true);
  };

  const onHide = () => {
    setOpen(false);
    setSelectedId(null);
  };

  const handleDeleteConfirm = () => {
    if (!selectedId) return;

    dispatch(deleteReviewAction({ id: selectedId }));

    setOpen(false);
    setSelectedId(null);
  };

  return (
    <>
      <Notification />
      <Box
        sx={{
          bgcolor: "#fff",
          border: "1px solid #d1cfcf",
          borderRadius: "5px",
          overflow: "hidden",
          marginTop: "10px",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "6px 8px",
          }}
        >
          <Typography sx={{ fontSize: "18px", fontWeight: "600" }}>
            Review Source
          </Typography>

          {/* Input + Button */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              p: 1,
            }}
          >
            <TextField
              // label="Source Name"
              size="small"
              value={name}
              error={!!nameError}
              helperText={nameError}
              placeholder="Enter Source Name"
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) setNameError("");
              }}
              sx={{
                width: 250,
                "& .MuiInputBase-root": {
                  height: 30,
                },
                "& .MuiInputBase-input": {
                  padding: "0px 10px",
                },
              }}
            />
            <CustomButton
              variant="contained"
              title={"Save"}
              disabled={!name.trim()}
              onClick={handleSave}
            />
          </Box>
        </Box>

        {/* Table */}
        <Box
          sx={{
            bgcolor: "#fff",
            height: { xs: "auto", sm: "auto" },
            border: "1px solid #d1cfcf",
            borderRadius: "5px",
            overflow: "auto",
          }}
        >
          <TableDesign
            tableHeadings={tableHeadings}
            tableHeadColor={"#02906f"}
            cellColor={"#fff"}
            pagination={null}
            isLoader={false}
            stickyHeader={true}
            filterOption={filterOption}
            setFilterOption={setFilterOption}
            extraCSS={{ height: "100%" }}
          >
            {reviewSource?.length > 0 ? (
              reviewSource.map((item: any, index: number) => (
                <TableRow
                  key={item.id}
                  sx={{
                    height: 36,
                    "&:hover": {
                      backgroundColor: "#f0f0f0",
                    },
                  }}
                >
                  <TableCell sx={{ py: 0.5 }}>{index + 1}</TableCell>

                  <TableCell sx={{ py: 0.5 }}>{item.name}</TableCell>

                  <TableCell align="center" sx={{ py: 0.5 }}>
                    {item.is_deletable && (
                      <IconButton
                        onClick={() => handleDeleteClick(item.id)}
                        color="error"
                        size="small"
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={tableHeadings.length}>
                  <Box
                    sx={{
                      height: "20vh",
                      display: "flex",
                      justifyContent: "center",
                      position: "relative",
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        fontSize: "1rem",
                        color: "#4A4A4A",
                        // top: "100px",
                      }}
                    >
                      No Review Source Found.
                    </span>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableDesign>
        </Box>
      </Box>
      {open && (
        <AlertPopup
          open={open}
          onHide={onHide}
          title={"Delete Review Source"}
          content={popupContent.delete}
          submitHandler={handleDeleteConfirm}
        />
      )}
    </>
  );
};

export default ReviewSource;
